'use client';

/**
 * The journey map and the per-level lesson list.
 *
 * The map is a vertical path rather than a grid because a path communicates
 * *sequence* — a child can see they are on step 4 of 15 and what comes next,
 * which a grid of equal tiles does not convey. Nodes alternate left and right
 * so the eye follows the line rather than scanning columns.
 */
import React from 'react';
import Link from 'next/link';
import { Lock, Check, Play, Star } from 'lucide-react';
import { LEVELS } from '../data/curriculum';
import { isLessonUnlocked, isLevelUnlocked, nextUpLesson } from '../lib/progress';
import { useKids } from '../context/KidsProvider';
import type { LessonDef, LevelDef } from '../types';
import { ProgressRail } from './ui';

const KIND_LABEL: Record<LessonDef['kind'], { label: string; emoji: string }> = {
  keys: { label: 'Key drill', emoji: '🎹' },
  words: { label: 'Words', emoji: '📝' },
  sentences: { label: 'Sentences', emoji: '💬' },
  paragraph: { label: 'Paragraph', emoji: '📄' },
  story: { label: 'Story', emoji: '📖' },
  timed: { label: 'Timed', emoji: '⏱️' },
  boss: { label: 'Challenge', emoji: '🏆' },
};

/* ──────────────────────────────────────────────────────────── level node ── */

function LevelNode({ level, side }: { level: LevelDef; side: 'left' | 'right' }) {
  const { profile } = useKids();
  const unlocked = isLevelUnlocked(profile, level.id);
  const done = level.lessons.filter((l) => profile?.lessons[l.id]?.completed).length;
  const stars = level.lessons.reduce((sum, l) => sum + (profile?.lessons[l.id]?.stars ?? 0), 0);
  const complete = done === level.lessons.length;
  const inProgress = done > 0 && !complete;

  const body = (
    <>
      <span
        className="grid place-items-center rounded-full shrink-0 relative"
        style={{
          width: 64,
          height: 64,
          fontSize: 28,
          background: complete
            ? `var(${level.color})`
            : unlocked
              ? `color-mix(in srgb, var(${level.color}) 18%, var(--card))`
              : 'var(--muted)',
          border: `3px solid ${unlocked ? `var(${level.color})` : 'var(--border-strong)'}`,
          boxShadow: inProgress
            ? `0 0 0 6px color-mix(in srgb, var(${level.color}) 20%, transparent)`
            : undefined,
        }}
        aria-hidden="true"
      >
        {unlocked ? level.emoji : <Lock size={22} />}
        {complete && (
          <span
            className="absolute grid place-items-center rounded-full"
            style={{
              right: -4,
              bottom: -4,
              width: 24,
              height: 24,
              background: 'var(--k-mint)',
              color: '#fff',
              border: '2px solid var(--card)',
            }}
          >
            <Check size={13} strokeWidth={3.5} />
          </span>
        )}
      </span>

      <span className={`min-w-0 flex-1 ${side === 'right' ? 'sm:text-right' : ''}`}>
        <span className="k-eyebrow">Level {level.index}</span>
        <span className="block font-extrabold text-lg leading-tight mt-0.5">{level.title}</span>
        <span className="block text-sm text-muted-foreground">{level.subtitle}</span>
        <span
          className={`flex items-center gap-1.5 mt-2 text-xs font-extrabold ${
            side === 'right' ? 'sm:justify-end' : ''
          }`}
        >
          <Star
            size={13}
            fill="currentColor"
            style={{ color: 'var(--k-mango)' }}
            aria-hidden="true"
          />
          <span className="tabular-nums">
            {stars}/{level.lessons.length * 3}
          </span>
          <span className="text-muted-foreground font-bold">
            · {done}/{level.lessons.length} lessons
          </span>
        </span>
      </span>
    </>
  );

  const shared = `k-card-interactive w-full flex items-center gap-4 p-4 ${
    side === 'right' ? 'sm:flex-row-reverse sm:text-right' : ''
  }`;

  if (!unlocked) {
    return (
      <div className={`${shared} k-locked`} aria-disabled="true">
        {body}
        <span className="sr-only">Locked. Finish the previous level to unlock {level.title}.</span>
      </div>
    );
  }

  return (
    <Link href={`/kids/journey/${level.slug}`} className={shared}>
      {body}
    </Link>
  );
}

/* ───────────────────────────────────────────────────────────── the map ──── */

export function JourneyMap() {
  const { profile, hydrated } = useKids();
  const nextId = nextUpLesson(profile);
  const totalDone = LEVELS.flatMap((l) => l.lessons).filter(
    (l) => profile?.lessons[l.id]?.completed
  ).length;
  const totalLessons = LEVELS.flatMap((l) => l.lessons).length;

  return (
    <div>
      <div className="k-card-lg p-5 mb-8">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
          <div>
            <p className="k-eyebrow">Your journey</p>
            <p className="text-2xl font-extrabold mt-0.5 tabular-nums">
              {totalDone}{' '}
              <span className="text-muted-foreground text-lg">/ {totalLessons} lessons</span>
            </p>
          </div>
          {hydrated && (
            <Link href={`/kids/lesson/${nextId}`} className="k-btn k-btn-primary">
              <Play size={18} aria-hidden="true" />
              {totalDone === 0 ? 'Start Level 1' : 'Continue'}
            </Link>
          )}
        </div>
        <ProgressRail value={totalDone} max={totalLessons} label="Course progress" showValue />
      </div>

      <ol className="relative">
        {LEVELS.map((level, i) => {
          const side = i % 2 === 0 ? 'left' : 'right';
          const done = level.lessons.every((l) => profile?.lessons[l.id]?.completed);
          return (
            <li key={level.id} className="relative pb-5">
              {i < LEVELS.length - 1 && (
                <span
                  className={`k-path hidden sm:block ${done ? 'k-path-done' : ''}`}
                  style={{ top: 76, bottom: -8 }}
                  aria-hidden="true"
                />
              )}
              <div className={`sm:w-[calc(50%+40px)] ${side === 'right' ? 'sm:ml-auto' : ''}`}>
                <LevelNode level={level} side={side} />
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

/* ────────────────────────────────────────────────────────── lesson list ─── */

export function LessonList({ level }: { level: LevelDef }) {
  const { profile } = useKids();

  return (
    <ol className="grid gap-2.5">
      {level.lessons.map((lesson, i) => {
        const record = profile?.lessons[lesson.id];
        const unlocked = isLessonUnlocked(profile, lesson.id);
        const kind = KIND_LABEL[lesson.kind];
        const assigned = profile?.assigned.includes(lesson.id);

        const inner = (
          <>
            <span
              className="grid place-items-center rounded-2xl shrink-0 font-extrabold"
              style={{
                width: 46,
                height: 46,
                fontSize: 19,
                background: record?.completed
                  ? 'var(--k-mint)'
                  : unlocked
                    ? `color-mix(in srgb, var(${level.color}) 15%, var(--card))`
                    : 'var(--muted)',
                color: record?.completed ? '#fff' : 'inherit',
                border: `2px solid ${unlocked ? `var(${level.color})` : 'var(--border-strong)'}`,
              }}
              aria-hidden="true"
            >
              {record?.completed ? (
                <Check size={20} strokeWidth={3} />
              ) : unlocked ? (
                i + 1
              ) : (
                <Lock size={17} />
              )}
            </span>

            <span className="min-w-0 flex-1">
              <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <span className="font-extrabold">{lesson.title}</span>
                <span
                  className="k-pill"
                  style={{ background: 'var(--muted)', fontSize: '0.68rem' }}
                >
                  {kind.emoji} {kind.label}
                </span>
                {assigned && (
                  <span
                    className="k-pill"
                    style={{
                      background: 'var(--k-sky-soft)',
                      color: 'var(--k-sky)',
                      fontSize: '0.68rem',
                    }}
                  >
                    📌 Assigned
                  </span>
                )}
              </span>
              <span className="block text-sm text-muted-foreground mt-0.5">
                Target {lesson.targetWpm} wpm · {lesson.targetAccuracy}% accuracy
                {lesson.durationSec ? ` · ${lesson.durationSec}s` : ''}
              </span>
            </span>

            <span className="shrink-0 text-right">
              <span className="block text-sm" aria-label={`${record?.stars ?? 0} of 3 stars`}>
                {[1, 2, 3].map((n) => (
                  <span
                    key={n}
                    style={{ opacity: n <= (record?.stars ?? 0) ? 1 : 0.22 }}
                    aria-hidden="true"
                  >
                    ⭐
                  </span>
                ))}
              </span>
              {record && (
                <span className="block text-xs text-muted-foreground mt-0.5 tabular-nums">
                  best {record.bestWpm} wpm
                </span>
              )}
            </span>
          </>
        );

        return (
          <li key={lesson.id}>
            {unlocked ? (
              <Link
                href={`/kids/lesson/${lesson.id}`}
                className="k-card-interactive flex items-center gap-3.5 p-3.5"
              >
                {inner}
              </Link>
            ) : (
              <div className="k-card k-locked flex items-center gap-3.5 p-3.5" aria-disabled="true">
                {inner}
              </div>
            )}
          </li>
        );
      })}
    </ol>
  );
}
